import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext.js";
import styles from "./ProtectedRoute.module.css";

interface Props {
  children: ReactNode;
  /** i18n key describing why login is needed, e.g. "auth.loginForCatch" */
  reason?: string;
}

export default function ProtectedRoute({ children, reason }: Props) {
  const { isLoading, isChecking, hasUsableSession, login, register } =
    useAuth();
  const { t } = useTranslation();

  // hasUsableSession is also true when offline with a session previously
  // seen on this device — see AuthContext's hadRecentSession — since the
  // check-sso round trip that would normally gate this route can't succeed
  // without a network anyway. Don't make a fisher stare at a login screen
  // (or a 10s spinner) when they just need to queue a catch offline.
  if ((isLoading || isChecking) && !hasUsableSession) {
    return (
      <div className={styles.center}>
        <div className={styles.spinner} />
        <p className={styles.loadingText}>{t("common.loading")}</p>
      </div>
    );
  }

  if (!hasUsableSession) {
    return (
      <div className={styles.center}>
        <div className={styles.card}>
          <span className={styles.logo}>Lakseborsen</span>
          <h2 className={styles.title}>
            {reason ? t(reason) : t("auth.loginRequired")}
          </h2>
          <p className={styles.hint}>{t("auth.loginHint")}</p>
          <button className={styles.loginBtn} onClick={login}>
            {t("nav.login")}
          </button>
          <p className={styles.registerRow}>
            {t("auth.registerHint")}{" "}
            <button className={styles.registerLink} onClick={register}>
              {t("nav.registerLink")}
            </button>
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
